/**
 * ActiveSessionsTable — Founder admin view of every active session
 * across all members of this workspace.
 *
 * V1 surface: device (raw user-agent string), member, session age,
 * revoke action. Geo-IP "Pune, MH" lookup is V1.x.
 *
 * "Sign out everywhere" sits in the header — Founder-only, hits
 * `team.revokeAllSessions`.
 */
import { LogOut, Monitor } from "lucide-react";
import { Button, StatusPill } from "@flint/ui";
import { DataTableCard, ListPagination } from "../../master-list/index.ts";
import { formatRelativeTime } from "../../../lib/format.ts";

export interface SessionRow {
  sessionId: string;
  userId: string;
  userName: string;
  userPhone: string;
  expiresAt: string;
  issuedAt: string;
  userAgent: string | null;
  ip: string | null;
}

export interface ActiveSessionsTableProps {
  sessions: ReadonlyArray<SessionRow>;
  /** True when the actor is a Founder. Hides destructive bulk action otherwise. */
  isFounder: boolean;
  busy?: boolean;
  onRevoke: (sessionId: string) => void;
  onRevokeAll: () => void;
}

export function ActiveSessionsTable({
  sessions,
  isFounder,
  busy,
  onRevoke,
  onRevokeAll,
}: ActiveSessionsTableProps) {
  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start gap-3">
        <span
          aria-hidden
          className="inline-grid place-items-center h-8 w-8 rounded-sm bg-sky-50 text-sky-700 shrink-0"
        >
          <Monitor size={14} strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-ink leading-tight">Active sessions</h2>
          <p className="mt-0.5 text-xs text-muted leading-relaxed">
            Devices currently signed into this workspace. Revoke any you don&apos;t recognise.
          </p>
        </div>
        {isFounder ? (
          <Button
            intent="ghost"
            size="sm"
            onClick={onRevokeAll}
            disabled={busy}
            className="text-bad hover:bg-bad-50"
          >
            <LogOut size={13} strokeWidth={2} aria-hidden /> Sign out everywhere
          </Button>
        ) : null}
      </header>

      <DataTableCard
        footer={sessions.length > 0 ? [{ label: "Sessions", value: sessions.length }] : []}
        pagination={
          sessions.length > 0 ? (
            <ListPagination
              pageNumber={1}
              showingFrom={sessions.length === 0 ? 0 : 1}
              showingTo={sessions.length}
              total={sessions.length}
              sortLabel="most recent first"
              hasPrev={false}
              hasNext={false}
              onPrev={() => {}}
              onNext={() => {}}
            />
          ) : undefined
        }
      >
        <table className="w-full text-sm">
          <thead className="bg-bg/50">
            <tr className="text-left text-xs uppercase tracking-wider text-muted">
              <th className="px-5 py-2.5 font-medium">Device</th>
              <th className="px-3 py-2.5 font-medium">Member</th>
              <th className="px-3 py-2.5 font-medium">Started</th>
              <th className="px-3 py-2.5 font-medium">Status</th>
              <th className="px-5 py-2.5 font-medium text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {sessions.map((s) => (
              <tr key={s.sessionId} className="hover:bg-bg/40">
                <td className="px-5 py-3">
                  <div className="text-sm font-medium text-ink truncate max-w-[320px]">
                    {prettyAgent(s.userAgent) ?? "Unknown device"}
                  </div>
                  {s.ip ? (
                    <div className="text-xs text-muted font-mono mt-0.5">::{s.ip.slice(-8)}</div>
                  ) : null}
                </td>
                <td className="px-3 py-3 text-xs text-ink-2 whitespace-nowrap">{s.userName}</td>
                <td className="px-3 py-3 text-xs text-muted whitespace-nowrap">
                  {formatRelativeTime(s.issuedAt)}
                </td>
                <td className="px-3 py-3">
                  <StatusPill tone="ok" size="xs" dot>
                    Active
                  </StatusPill>
                </td>
                <td className="px-5 py-3 text-right">
                  {isFounder ? (
                    <Button
                      intent="ghost"
                      size="sm"
                      onClick={() => onRevoke(s.sessionId)}
                      disabled={busy}
                      className="text-bad hover:bg-bad-50"
                    >
                      Revoke
                    </Button>
                  ) : (
                    <span className="text-xs text-muted">—</span>
                  )}
                </td>
              </tr>
            ))}
            {sessions.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-10 text-center text-sm text-muted">
                  No active sessions.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </DataTableCard>
    </div>
  );
}

/** Best-effort one-liner from a UA string. Heuristic — V1.x parses
 *  with `ua-parser-js` for "Pixel 8 · Chrome" precision. */
function prettyAgent(ua: string | null): string | null {
  if (!ua) return null;
  if (/Macintosh/.test(ua)) return ua.includes("Safari") ? "Mac · Safari" : "Mac · Chrome";
  if (/iPhone/.test(ua)) return "iPhone · Safari";
  if (/Android/.test(ua)) return /Chrome/.test(ua) ? "Android · Chrome" : "Android · Browser";
  if (/Windows/.test(ua)) return /Chrome/.test(ua) ? "Windows · Chrome" : "Windows · Browser";
  return ua.slice(0, 80);
}
